import { useEffect, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startGetAdsCampaign } from "../../../actions/adsCampaigns";
import { ADS_CAMPAIGN_STATUS_DRAFT } from "../../../utils/constants";
import { useReportSnapshot } from "../../../features/reportPdf/ReportSnapshotContext";

import { LaunchError } from "../../../utils/errors";

const getNonDraftCampaignIDs = (campaigns = {}) =>
  Object.keys(campaigns).filter(
    (campaignID) =>
      campaigns[campaignID]?.states?.general !== ADS_CAMPAIGN_STATUS_DRAFT
  );

/**
 * Loads full campaign hierarchy (adsGroups + ads) for label resolution in charts.
 */
const useGlobalReportCampaignDetails = ({
  campaigns = {},
  enabled = false,
}) => {
  const dispatch = useDispatchApp();
  const snapshot = useReportSnapshot();
  const [detailsByCampaignID, setDetailsByCampaignID] = useState(
    snapshot?.campaignDetails ?? {}
  );
  const [attemptedCampaignIDs, setAttemptedCampaignIDs] = useState({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const requestEpochRef = useRef(0);

  useEffect(() => {
    if (snapshot || !enabled) {
      return;
    }

    const campaignIDs = getNonDraftCampaignIDs(campaigns);
    const missingIDs = campaignIDs.filter(
      (campaignID) => !attemptedCampaignIDs[campaignID]
    );

    if (!missingIDs.length) {
      setIsLoadingDetails(false);
      return;
    }

    const requestEpoch = ++requestEpochRef.current;
    setIsLoadingDetails(true);

    Promise.all(
      missingIDs.map((campaignID) => dispatch(startGetAdsCampaign(campaignID)))
    )
      .then((results) => {
        if (requestEpoch !== requestEpochRef.current) {
          return;
        }

        const next = {};
        const attempted = {};
        missingIDs.forEach((campaignID, index) => {
          attempted[campaignID] = true;
          const detail = results[index];
          if (detail) {
            next[campaignID] = detail;
          }
        });

        setAttemptedCampaignIDs((prev) => ({ ...prev, ...attempted }));
        if (Object.keys(next).length) {
          setDetailsByCampaignID((prev) => ({ ...prev, ...next }));
        }
      })
      .catch((error) => {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useGlobalReportCampaignDetails",
            capture: "exception",
          })
        );
        console.error("Error loading global report campaign details:", error);
      })
      .finally(() => {
        if (requestEpoch === requestEpochRef.current) {
          setIsLoadingDetails(false);
        }
      });
  }, [attemptedCampaignIDs, campaigns, dispatch, enabled, snapshot]);

  const findAdInCampaign = ({ campaignID, adID, campaign }) => {
    const detail = detailsByCampaignID[campaignID] || campaign;
    const adsGroups = detail?.adsGroups;
    if (!adsGroups) {
      return null;
    }

    const adGroupList = Array.isArray(adsGroups)
      ? adsGroups
      : Object.values(adsGroups);

    for (const adGroup of adGroupList) {
      const ads = adGroup?.ads;
      if (!ads) continue;

      if (!Array.isArray(ads) && ads[adID]) {
        return ads[adID];
      }

      const adList = Array.isArray(ads) ? ads : Object.values(ads);
      const found = adList.find((ad) => ad?.id === adID);
      if (found) {
        return found;
      }
    }

    return null;
  };

  const resolveAdLabel = ({ campaignID, adID, campaign, campaignName }) => {
    const ad = findAdInCampaign({ campaignID, adID, campaign });
    if (ad?.name) {
      return ad.name;
    }
    return campaignName || null;
  };

  const resolveAdMediaUrls = ({ campaignID, adID, campaign }) => {
    const ad = findAdInCampaign({ campaignID, adID, campaign });
    return Array.isArray(ad?.mediaUrls) ? ad.mediaUrls : [];
  };

  return {
    campaignDetails: detailsByCampaignID,
    isLoadingCampaignDetails: isLoadingDetails,
    resolveAdLabel,
    resolveAdMediaUrls,
  };
};

export default useGlobalReportCampaignDetails;
